import {rootState} from '@mintdata-wsp/common/state/RootState';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {CellSettingsBarMenu} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/CellSettingsBarState';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {ImgIcon} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcon';
import {ImgIcons} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcons';
import * as classnames from 'classnames/bind';
import {computed} from 'mobx';
import {inject, observer} from 'mobx-react';
import * as React from 'react';

const styles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/Mesh.less');
const cx = classnames.bind(styles);

interface IMeshSelectionFrameProps
{
    ctx?:IWSPMeshContext;
}

@inject('ctx')
@observer
export class MeshSelectionFrame extends React.Component<IMeshSelectionFrameProps>
{
    public render():JSX.Element
    {
        const {ctx} = this.props;

        const {isMenuOpened} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        return (
            <div>
                <div
                    style={this._enteredFrameCellStyle}
                    className={styles.enteredFrameCellSelection}
                >
                    <button
                        className={styles.selectionBtnRemove}
                        onMouseDown={this._handleRemoveMouseDown}
                    >
                        <ImgIcon icon={ImgIcons.close} className={styles.selectionImgIcon} />
                    </button>
                </div>
                {this._isFocusedMesh && <div
                    style={this._activeCellStyle}
                    className={styles.activeCellSelection}
                    data-component="active-cell"
                />}
                {this._isFocusedMesh && <div
                    style={this._style}
                    className={cx('selectionFrame', {noBorder: isMenuOpened == CellSettingsBarMenu.Border})}
                >
                    <div
                        className={styles.selectionFrameMarker}
                        onMouseDown={this._handleMouseDown(ctx)}
                    />
                </div>}
            </div>
        );
    }

    private _handleRemoveMouseDown = (event:React.MouseEvent<HTMLElement>) =>
    {
        event.stopPropagation();

        const {main, frameDetails, tabs} = controllers.ui.spreadsheets;

        const {ctx} = this.props;
        const {sheetId} = ctx;

        const sheet:AnySheet = main.findAnySheetById(sheetId, true);
        const activeSheet = tabs.getActiveSheet(sheet.workbook.id);

        frameDetails.exitNestedView(sheet.workbook.id, activeSheet.id, sheetId);
    };

    private _handleMouseDown(ctx:IWSPMeshContext)
    {
        return (event:React.MouseEvent<HTMLElement>):any =>
        {
            const {meshMouse} = controllers.ui.spreadsheets;

            meshMouse.onSelectionFrameMouseDown(ctx, event);

            event.stopPropagation();
        };
    }

    @computed
    private get _isFocusedMesh():boolean
    {
        const {workbookId, sheetId} = this.props.ctx;
        const {main} = controllers.ui.spreadsheets;

        const workbook:Workbook = main.findWorkbookById(workbookId);

        return workbook.view.focusedSheetId === sheetId;
    }

    // @computed
    private get _activeCellStyle():React.CSSProperties
    {
        const {ctx} = this.props;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {activeCellBounds} = meshState.details;

        if( !activeCellBounds )
            return {
                display: 'none'
            };

        const {x, y, width, height} = activeCellBounds;

        return {
            transform: `translate(${x}px, ${y}px)`,
            width: `${width}px`,
            height: `${height}px`
        };
    }

    // @computed
    private get _enteredFrameCellStyle():React.CSSProperties
    {
        const {ctx} = this.props;
        const {sheetId} = ctx;
        const {mesh, frameDetails} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const enteredFrameCell = frameDetails.getEnteredFrameCell(sheetId);

        const enteredFrameCellBounds = MeshUtils.getRangeBounds(enteredFrameCell, enteredFrameCell, meshState);

        if( !enteredFrameCellBounds )
        {
            return {
                display: 'none'
            };
        }

        const {x, y, width, height} = enteredFrameCellBounds;

        return {
            transform: `translate(${x}px, ${y}px)`,
            width: `${width}px`,
            height: `${height}px`
        };
    }

    // @computed
    private get _style():React.CSSProperties
    {
        const {ctx} = this.props;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {selectedRangeBounds, isSelectRangeMode} = meshState.details;
        const {isEditMode} = controllers.ui.spreadsheets.main;

        if( !selectedRangeBounds || isSelectRangeMode || isEditMode )
        {
            return {
                display: 'none'
            };
        }

        const {x, y, width, height} = selectedRangeBounds;

        return {
            transform: `translate(${x}px, ${y}px)`,
            width: `${width}px`,
            height: `${height}px`
        };
    }
}
